/**
 * Che (phủ đen) học liệu khi có dấu hiệu chụp/quay màn hình (plan 2026-10-03, Sếp chốt).
 *
 * Trình duyệt KHÔNG biết được phần mềm quay chạy nền (OBS…) — đây là lớp chặn "best effort",
 * đi kèm watermark (`ContentWatermark`) để truy vết:
 * - Cửa sổ mất focus / chuyển tab / thu nhỏ → phủ đen tới khi quay lại. Focus chuyển vào
 *   iframe CỦA bài học (SCORM, PDF) KHÔNG tính là mất focus (`document.hasFocus()` vẫn true).
 * - PrintScreen → phủ đen 3s + xoá clipboard (ảnh vừa chụp vào clipboard bị ghi đè).
 * - Đang giữ Win/Cmd + Shift (tổ hợp công cụ chụp: Win+Shift+S, Cmd+Shift+3/4/5) → phủ đen
 *   ngay trước khi kịp bấm phím thứ 3.
 * - In trang (Ctrl+P) → ẩn học liệu khi in.
 *
 * Bật bằng `CONTENT_BLACKOUT_ENABLED` (backend) → `user.feature_flags.content_blackout`.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "antd";
import { EyeInvisibleOutlined } from "@ant-design/icons";
import { useAuthStore } from "@/stores/auth";
import { ContentShieldContext, type ShieldReason, useContentShield } from "./contentShieldContext";

const SCREENSHOT_HIDE_MS = 3000;
const SHORTCUT_HIDE_MS = 1500;
const FOCUS_POLL_MS = 700;

export function ContentShieldProvider({ children }: { children: ReactNode }) {
  const enabled = useAuthStore((s) => !!s.user?.feature_flags?.content_blackout);
  const [reason, setReason] = useState<ShieldReason | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const hide = useCallback((r: ShieldReason, ms?: number) => {
    setReason(r);
    window.clearTimeout(timer.current);
    if (ms) {
      timer.current = window.setTimeout(
        () => setReason((cur) => (cur === r ? (document.hasFocus() ? null : "blur") : cur)),
        ms,
      );
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const clearClipboard = () => {
      navigator.clipboard?.writeText(" ").catch(() => undefined);
    };
    const onBlur = () =>
      window.setTimeout(() => {
        if (!document.hasFocus()) hide("blur");
      }, 0);
    const onFocus = () => setReason((cur) => (cur === "blur" || cur === "hidden" ? null : cur));
    const onVisibility = () => {
      if (document.hidden) hide("hidden");
      else if (document.hasFocus()) onFocus();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "PrintScreen") {
        hide("screenshot", SCREENSHOT_HIDE_MS);
        clearClipboard();
      } else if (e.type === "keydown" && e.shiftKey && (e.metaKey || e.key === "Meta")) {
        hide("screenshot", SHORTCUT_HIDE_MS);
      }
    };
    const onBeforePrint = () => hide("print");
    const onAfterPrint = () => setReason((cur) => (cur === "print" ? null : cur));
    // Mất focus khi đang ở trong iframe bài học (Alt+Tab từ SCORM) không bắn `blur` ở trang cha.
    const poll = window.setInterval(() => {
      if (!document.hasFocus()) {
        setReason((cur) => cur ?? "blur");
      } else {
        setReason((cur) => (cur === "blur" ? null : cur));
      }
    }, FOCUS_POLL_MS);

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("keyup", onKey, true);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(timer.current);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("keyup", onKey, true);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
      setReason(null);
    };
  }, [enabled, hide]);

  const value = useMemo(
    () =>
      enabled
        ? {
            hidden: reason !== null,
            reason,
            reveal: () => {
              if (document.hasFocus()) setReason(null);
            },
          }
        : null,
    [enabled, reason],
  );

  return (
    <ContentShieldContext.Provider value={value}>
      {enabled && (
        <style>{`@media print { [data-content-shield] { visibility: hidden !important; } }`}</style>
      )}
      {children}
    </ContentShieldContext.Provider>
  );
}

const MESSAGES: Record<ShieldReason, string> = {
  blur: "Nội dung bài học tạm ẩn khi cửa sổ không hoạt động. Bấm vào đây để tiếp tục học.",
  hidden: "Nội dung bài học tạm ẩn khi cửa sổ không hoạt động. Bấm vào đây để tiếp tục học.",
  screenshot: "Không được chụp / quay màn hình học liệu.",
  print: "Không được in học liệu.",
};

/** Lớp phủ đen — đặt trong khối `position: relative` (hoặc trong video player). */
export function BlackoutOverlay() {
  const shield = useContentShield();
  if (!shield?.hidden || !shield.reason) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={shield.reveal}
      onKeyDown={shield.reveal}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        background: "#000",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        textAlign: "center",
        cursor: "pointer",
        pointerEvents: "auto",
      }}
    >
      <EyeInvisibleOutlined style={{ fontSize: 36, color: "#8c8c8c" }} />
      <Typography.Text style={{ color: "#d9d9d9", maxWidth: 420 }}>
        {MESSAGES[shield.reason]}
      </Typography.Text>
    </div>
  );
}
